import { AlertDescription, Typography, Separator, Loading, Button, Alert } from '@ui/components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { SubscriptionCard } from '@shared/components';
import { Warning } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { cn } from '@ui/utils';

import { useOnboardingSubscriptions } from './use-onboarding-subscriptions';

export const OnboardingSubscriptions = () => {
  const { t } = useTranslation();

  const {
    isInitialDataLoading,
    isMutationLoading,
    subscriptionsData,
    isQueryLoading,
    initialData,
    onSubmit,
  } = useOnboardingSubscriptions();

  const [selectedSubscription, setSelectedSubscription] = useState<string | null>(null);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

  const handleSubmit = () => {
    if (!selectedSubscription) {
      setShowErrorMessage(true);
      return;
    }

    onSubmit(selectedSubscription);
  };

  useDeepCompareEffect(() => {
    const defaultSubscription = initialData?.items.find((subscription) => subscription.isDefault);
    if (defaultSubscription) setSelectedSubscription(defaultSubscription.code);
  }, [initialData?.items]);

  if (isInitialDataLoading || isQueryLoading) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      <Typography variant="h400" element="h1">
        {t('ONBOARDING.SUBSCRIPTIONS.TITLE')}
      </Typography>

      <Typography variant="labelLargeBook" className="mt-8">
        {t('ONBOARDING.SUBSCRIPTIONS.DESCRIPTION')}
      </Typography>

      {showErrorMessage && (
        <Alert variant="critical" className="mt-8">
          <AlertDescription className="flex gap-2 items-center">
            <Warning className="inline-block" />
            {t('ONBOARDING.SUBSCRIPTIONS.SELECT_SUBSCRIPTION_ERROR')}
          </AlertDescription>
        </Alert>
      )}

      <section className="h-full w-full overflow-auto mt-8">
        <div className="flex gap-6 w-full pb-4">
          {initialData?.items.map((subscription) => (
            <div key={subscription.id} className="p-1">
              <SubscriptionCard
                className={cn('border-none outline outline-border-default min-w-76', {
                  'outline-2 outline-surface': subscription.code === selectedSubscription,
                })}
                onSelect={() => {
                  setSelectedSubscription(subscription.code);
                  setShowErrorMessage(false);
                }}
                isSelected={subscription.code === selectedSubscription}
                numOfEmployees={subscriptionsData?.numOfEmployees}
                numOfVehicles={subscriptionsData?.numOfVehicles}
                {...subscription}
              />
            </div>
          ))}

          {initialData?.hasTbaService && (
            <div className="p-1">
              <SubscriptionCard
                className="border-none outline outline-border-default min-w-76"
                name={t('SUBSCRIPTIONS.SOON')}
                isLoading={true}
              />
            </div>
          )}
        </div>
      </section>

      <Separator className="my-8" />

      <Button
        isLoading={isMutationLoading}
        disabled={isMutationLoading}
        onClick={handleSubmit}
        variant="primary"
        size="large"
        wide
      >
        {t('ONBOARDING.SUBSCRIPTIONS.SAVE_AND_CONTINUE')}
      </Button>
    </div>
  );
};
